import { useMemo } from 'react';
import { Box, Stack, Typography, LinearProgress, Chip, Tooltip } from '@mui/material';
import type { SourceDocHeader, ScanEntry } from '../../types/warehouse';

interface DocumentProgressProps {
  doc: SourceDocHeader;
  entries: ScanEntry[];
}

interface LineProgress {
  lineNum: number;
  itemCode: string;
  openQty: number;
  scanned: number;
  state: 'outstanding' | 'partial' | 'complete' | 'over';
}

/** Per-line scanned-vs-open progress for the anchored document. */
export default function DocumentProgress({ doc, entries }: DocumentProgressProps): JSX.Element {
  const lines = useMemo<LineProgress[]>(() => {
    const scannedByLine = new Map<number, number>();
    for (const e of entries) {
      if (e.sourceDocEntry === doc.docEntry && e.sourceDocLineNum != null) {
        scannedByLine.set(e.sourceDocLineNum, (scannedByLine.get(e.sourceDocLineNum) ?? 0) + 1);
      }
    }
    return doc.lines.map((l) => {
      const scanned = scannedByLine.get(l.lineNum) ?? 0;
      let state: LineProgress['state'] = 'outstanding';
      if (scanned === 0) state = 'outstanding';
      else if (scanned >= l.openQty && l.openQty > 0) state = scanned > l.openQty ? 'over' : 'complete';
      else state = 'partial';
      return { lineNum: l.lineNum, itemCode: l.itemCode, openQty: l.openQty, scanned, state };
    });
  }, [doc, entries]);

  const counts = useMemo(() => {
    const c = { complete: 0, partial: 0, outstanding: 0, over: 0 };
    for (const l of lines) c[l.state] += 1;
    return c;
  }, [lines]);

  const totalOpen = lines.reduce((s, l) => s + Math.max(l.openQty, 0), 0);
  const totalScanned = lines.reduce((s, l) => s + l.scanned, 0);
  const pct = totalOpen > 0 ? Math.min(100, Math.round((totalScanned / totalOpen) * 100)) : 0;

  const stateColor: Record<LineProgress['state'], 'success' | 'warning' | 'default' | 'error'> = {
    complete: 'success',
    partial: 'warning',
    outstanding: 'default',
    over: 'error',
  };

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Chip size="small" color="success" label={`${counts.complete} complete`} />
        <Chip size="small" color="warning" label={`${counts.partial} partial`} />
        <Chip size="small" label={`${counts.outstanding} outstanding`} />
        {counts.over > 0 && <Chip size="small" color="error" label={`${counts.over} over`} />}
      </Stack>
      <LinearProgress variant="determinate" value={pct} sx={{ height: 8, borderRadius: 1, mb: 0.5 }} />
      <Typography variant="caption" color="text.secondary">
        {totalScanned} / {totalOpen} units scanned ({pct}%)
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5 }}>
        {lines.map((l) => (
          <Tooltip key={l.lineNum} title={`Line ${l.lineNum} · ${l.itemCode} · ${l.scanned}/${l.openQty}`}>
            <Chip
              size="small"
              variant={l.state === 'outstanding' ? 'outlined' : 'filled'}
              color={stateColor[l.state]}
              label={`${l.itemCode} ${l.scanned}/${l.openQty}`}
            />
          </Tooltip>
        ))}
      </Stack>
    </Box>
  );
}
